import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import {
  useFiscalMonthSettingsStore,
  getFiscalMonthRange,
} from '../stores/fiscalMonthSettingsStore';
import { format } from 'date-fns';

// shadcn/ui components
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import { Switch } from './ui/switch';

// Lucide React icons
import { Calendar, RefreshCw, Info, Loader2, Save as SaveIcon } from 'lucide-react';

const FiscalMonthSettings: React.FC = () => {
  const { translate } = useTranslation();
  const { settings, setSettings, resetToDefault, loadSettings, isLoading, isSaving, error, hasLoadedFromAPI } =
    useFiscalMonthSettingsStore();
  const [previewDate, setPreviewDate] = useState(new Date());
  
  // Local draft state for manual saving
  // Ensure draftSettings is always a complete object
  const [draftSettings, setDraftSettings] = useState<typeof settings>(() => ({
    enabled: settings.enabled ?? false,
    startDay: settings.startDay ?? 1,
  }));
  const [startDayInput, setStartDayInput] = useState<string>(settings.startDay.toString());

  // Load settings from API on mount
  useEffect(() => {
    if (!hasLoadedFromAPI && !isLoading) {
      loadSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync draft settings when store settings change (e.g. after load or reset)
  // But only if we're not currently saving to avoid overwriting user's changes
  // Also ensure we have a complete settings object
  useEffect(() => {
    if (!isSaving && settings) {
      const completeSettings = {
        enabled: settings.enabled ?? false,
        startDay: settings.startDay ?? 1,
      };
      setDraftSettings(completeSettings);
      setStartDayInput(completeSettings.startDay.toString());
    }
  }, [settings, isSaving]);

  const handleDraftChange = (
    key: keyof typeof settings,
    value: any
  ) => {
    setDraftSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    try {
        console.log('[FiscalMonthSettings] Saving draftSettings', draftSettings);
        // Ensure we're passing a complete settings object
        const completeDraftSettings: typeof settings = {
          enabled: draftSettings.enabled ?? settings.enabled,
          startDay: draftSettings.startDay ?? settings.startDay,
        };
        console.log('[FiscalMonthSettings] Complete settings to save', completeDraftSettings);
        await setSettings(completeDraftSettings);
        // After successful save, the settings from store will be updated
        // The useEffect will sync draftSettings after isSaving becomes false
    } catch (err) {
        console.error('[FiscalMonthSettings] Failed to save settings:', err);
    }
  };

  const handleReset = async () => {
    try {
      await resetToDefault();
    } catch (err) {
      console.error('Failed to reset settings:', err);
    }
  };

  // Calculate preview fiscal month range based on DRAFT settings
  const previewRange = useMemo(() => {
    if (!draftSettings.enabled) {
      return null;
    }
    return getFiscalMonthRange(previewDate, draftSettings);
  }, [previewDate, draftSettings]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar className="h-6 w-6 text-primary" />
            <div>
              <CardTitle>
                {translate(
                  'pos.settings.fiscalMonth.title',
                  'Fiscal Month Settings'
                )}
              </CardTitle>
              <CardDescription className="mt-1">
                {translate(
                  'pos.settings.fiscalMonth.subtitle',
                  'Configure custom fiscal month periods for reporting'
                )}
              </CardDescription>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleReset} disabled={isSaving}>
                <RefreshCw className="h-4 w-4 mr-2" />
                {translate('pos.settings.fiscalMonth.reset', 'Reset')}
            </Button>
            <Button size="sm" onClick={handleSave} disabled={isSaving || isLoading}>
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <SaveIcon className="h-4 w-4 mr-2" />}
                {translate('pos.common.save', 'Save')}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <Info className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {(isLoading || isSaving) && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              {isLoading ? 'Loading settings...' : 'Saving settings...'}
            </AlertDescription>
          </Alert>
        )}
        
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            {translate(
              'pos.settings.fiscalMonth.info',
              'Fiscal months allow you to define custom monthly periods that don\'t align with calendar months. For example, a fiscal month starting on the 15th runs from the 15th of one month to the 14th of the next month.'
            )}
          </AlertDescription>
        </Alert>

        {/* Enable/Disable Fiscal Months */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="space-y-0.5">
            <Label htmlFor="fiscal-enabled" className="text-base">
              {translate(
                'pos.settings.fiscalMonth.enable',
                'Enable Fiscal Months'
              )}
            </Label>
            <p className="text-sm text-muted-foreground">
              {translate(
                'pos.settings.fiscalMonth.enableDescription',
                'Use custom fiscal month periods instead of calendar months'
              )}
            </p>
          </div>
          <Switch
            id="fiscal-enabled"
            checked={draftSettings.enabled}
            onCheckedChange={(checked) =>
              handleDraftChange('enabled', checked)
            }
          />
        </div>

        {draftSettings.enabled && (
          <>
            <Separator />

            {/* Start Day Configuration */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">
                {translate(
                  'pos.settings.fiscalMonth.configuration',
                  'Configuration'
                )}
              </h3>

              <div className="space-y-2">
                <Label htmlFor="fiscal-start-day">
                  {translate(
                    'pos.settings.fiscalMonth.startDay',
                    'Fiscal Month Start Day'
                  )}
                </Label>
                <div className="flex items-center gap-4">
                  <Input
                    id="fiscal-start-day"
                    type="number"
                    min="1"
                    max="31"
                    value={startDayInput}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Update local input state immediately
                      setStartDayInput(value);
                      
                      const numValue = parseInt(value, 10);
                      if (!isNaN(numValue) && numValue >= 1 && numValue <= 31) {
                         handleDraftChange('startDay', numValue);
                      }
                    }}
                    onBlur={(e) => {
                      const value = e.target.value;
                      const numValue = parseInt(value, 10);
                      if (isNaN(numValue) || numValue < 1 || numValue > 31) {
                        const validValue = Math.max(1, Math.min(31, numValue || draftSettings.startDay));
                        setStartDayInput(validValue.toString());
                        if (validValue !== draftSettings.startDay) {
                           handleDraftChange('startDay', validValue);
                        }
                      } else {
                        setStartDayInput(numValue.toString());
                      }
                    }}
                    className="w-24"
                  />
                  <p className="text-sm text-muted-foreground">
                    {translate(
                      'pos.settings.fiscalMonth.startDayHelp',
                      'Day of the month when each fiscal month begins (1-31)'
                    )}
                  </p>
                </div>
              </div>

              {/* Quick Presets */}
              <div className="space-y-2">
                <Label>
                  {translate(
                    'pos.settings.fiscalMonth.presets',
                    'Quick Presets'
                  )}
                </Label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                        handleDraftChange('startDay', 1);
                        setStartDayInput('1');
                    }}
                  >
                    {translate('pos.settings.fiscalMonth.calendarMonth', 'Calendar (1st)')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                        handleDraftChange('startDay', 15);
                        setStartDayInput('15');
                    }}
                  >
                    {translate('pos.settings.fiscalMonth.midMonth', 'Mid-Month (15th)')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                        handleDraftChange('startDay', 25);
                        setStartDayInput('25');
                    }}
                  >
                    {translate('pos.settings.fiscalMonth.endMonth', 'End-Month (25th)')}
                  </Button>
                </div>
              </div>
            </div>

            <Separator />

            {/* Preview */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">
                {translate('pos.settings.fiscalMonth.preview', 'Preview')}
              </h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="preview-date">
                    {translate(
                      'pos.settings.fiscalMonth.previewDate',
                      'Preview Date'
                    )}
                  </Label>
                  <Input
                    id="preview-date"
                    type="date"
                    value={format(previewDate, 'yyyy-MM-dd')}
                    onChange={(e) =>
                      setPreviewDate(new Date(e.target.value))
                    }
                    className="w-48"
                  />
                </div>

                {previewRange && (
                  <div className="p-4 border rounded-lg bg-muted/50">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">
                          {translate(
                            'pos.settings.fiscalMonth.fiscalPeriod',
                            'Fiscal Period'
                          )}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {format(previewDate, 'MMM d, yyyy')}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">
                            {translate(
                              'pos.settings.fiscalMonth.start',
                              'Start'
                            )}
                            :
                          </span>
                          <p className="font-medium">
                            {format(previewRange.start, 'MMM d, yyyy')}
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">
                            {translate('pos.settings.fiscalMonth.end', 'End')}:
                          </span>
                          <p className="font-medium">
                            {format(previewRange.end, 'MMM d, yyyy')}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        {translate(
                          'pos.settings.fiscalMonth.previewDescription',
                          'This shows the fiscal month period that contains the selected date'
                        )}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {!draftSettings.enabled && (
          <div className="p-4 border rounded-lg bg-muted/30">
            <p className="text-sm text-muted-foreground">
              {translate(
                'pos.settings.fiscalMonth.disabledMessage',
                'Fiscal months are currently disabled. Enable them above to configure custom monthly periods.'
              )}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FiscalMonthSettings;

